import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import {
  useVehicleOperationServiceV1GetVehicleOperationQuery,
  useVehicleOperationServiceV1UpdateVehicleOperationMutation,
  useVehicleOperationServiceV1GetVehicleOperationTripTypesQuery,
  useVehicleOperationServiceV1GetVehicleOperationVehicleAuxiliariesQuery,
  useVehicleOperationServiceV1GetVehicleOperationVehicleModelsQuery,
  useVehicleOperationServiceV1GetVehicleOperationVehicleTypesQuery,
  type VehicleOperationServiceV1UpdateVehicleOperationBody,
} from "_services/modules/vehicle_operation";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

export interface EditVehicleOperationFormValues {
  vehicleTypeId: string;
  vehicleModelId: string;
  vehicleAttachmentIds: string[];
  tripTypeId: string;
  fuelUsage: string;
  avgSpeed: string;
  workingHours: string;
}

const schema = yup.object({
  vehicleTypeId: yup.string().required("Vehicle Type is required"),
  vehicleModelId: yup.string().required("Vehicle Model is required"),
  vehicleAttachmentIds: yup.array().of(yup.string().required()).default([]),
  tripTypeId: yup.string().required("Trip Type is required"),
  fuelUsage: yup.string().required("Fuel Usage is required").test(
    "is-positive-number",
    "Must be a positive number",
    (value) => {
      if (!value) return false;
      const num = Number(value);
      return !isNaN(num) && num > 0;
    }
  ),
  avgSpeed: yup.string().required("Avg. Speed is required").test(
    "is-positive-number",
    "Must be a positive number",
    (value) => {
      if (!value) return false;
      const num = Number(value);
      return !isNaN(num) && num > 0;
    }
  ),
  workingHours: yup.string().required("Working Hours is required").test(
    "is-positive-number",
    "Must be a positive number",
    (value) => {
      if (!value) return false;
      const num = Number(value);
      return !isNaN(num) && num > 0;
    }
  ),
}).required();

const useEditVehicleOperationForm = (id: string | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("vehicleOperation");

  const { data: detail } = useVehicleOperationServiceV1GetVehicleOperationQuery(
    { id: id || "" },
    { skip: !id }
  );
  const [updateVehicleOperationConfig] = useVehicleOperationServiceV1UpdateVehicleOperationMutation();

  const { data: vehicleTypesData } = useVehicleOperationServiceV1GetVehicleOperationVehicleTypesQuery();
  const { data: vehicleAuxiliaryData } = useVehicleOperationServiceV1GetVehicleOperationVehicleAuxiliariesQuery();

  const vehicleTypes = vehicleTypesData?.vehicle_types ?? [];
  const attachmentOptions = vehicleAuxiliaryData?.vehicle_auxiliaries?.map((a) => ({
    id: a.id ?? "",
    name: a.name ?? "",
  })) ?? [];

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<EditVehicleOperationFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      vehicleTypeId: "",
      vehicleModelId: "",
      vehicleAttachmentIds: [],
      tripTypeId: "",
      fuelUsage: "",
      avgSpeed: "",
      workingHours: "",
    },
  });

  const skipModelReset = useRef(true);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingData, setPendingData] = useState<EditVehicleOperationFormValues | null>(null);

  const vehicleTypeId = watch("vehicleTypeId");
  const selectedVehicleType = vehicleTypes.find((vt) => vt.id === vehicleTypeId);
  const showAttachment = !!(selectedVehicleType as any)?.is_add_on;

  const { data: vehicleModelsData } = useVehicleOperationServiceV1GetVehicleOperationVehicleModelsQuery(
    { vehicleTypeId },
    { skip: !vehicleTypeId }
  );
  const { data: tripTypesData } = useVehicleOperationServiceV1GetVehicleOperationTripTypesQuery(
    { vehicleTypeId },
    { skip: !vehicleTypeId }
  );

  const vehicleModelOptions = vehicleModelsData?.vehicle_models ?? [];
  const tripTypes = tripTypesData?.trip_types ?? [];

  useEffect(() => {
    if (skipModelReset.current) {
      skipModelReset.current = false;
      return;
    }
    setValue("vehicleModelId", "");
    setValue("vehicleAttachmentIds", []);
  }, [vehicleTypeId, setValue]);

  useEffect(() => {
    if (detail) {
      skipModelReset.current = true;
      setValue("vehicleTypeId", detail.vehicle_type?.id ?? "");
      setValue("vehicleModelId", detail.vehicle_model?.id ?? "");
      setValue("vehicleAttachmentIds", detail.vehicle_attachments?.map((a) => a.id ?? "") ?? []);
      setValue("tripTypeId", detail.trip_type?.id ?? "");
      setValue("fuelUsage", String(detail.fuel_usage ?? ""));
      setValue("avgSpeed", String(detail.avg_speed ?? ""));
      setValue("workingHours", String(detail.working_hours ?? ""));
    }
  }, [detail, setValue]);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const onSubmit = (data: EditVehicleOperationFormValues) => {
    setPendingData(data);
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    if (!pendingData || !id) return;
    try {
      const payload: VehicleOperationServiceV1UpdateVehicleOperationBody = {
        vehicle_type_id: pendingData.vehicleTypeId,
        vehicle_model_id: pendingData.vehicleModelId,
        vehicle_attachment_ids: pendingData.vehicleAttachmentIds,
        trip_type_id: pendingData.tripTypeId,
        fuel_usage: Number(pendingData.fuelUsage),
        avg_speed: Number(pendingData.avgSpeed),
        rest_hours: 0,
        working_hours: Number(pendingData.workingHours),
      };
      await updateVehicleOperationConfig({ id, vehicleOperationServiceV1UpdateVehicleOperationBody: payload }).unwrap();
      toast.success(t("toast-update-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      if (error?.status === 409) {
        setError("tripTypeId", {
          type: "validate",
          message: t("error-duplicate-exists"),
        });
      } else if (error?.status === 404) {
        setShowSaveModal(false);
        navigate(-1);
      } else {
        errorHandler(error);
      }
      setShowSaveModal(false);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    control,
    handleSubmit,
    register,
    errors,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    showAttachment,
    vehicleModelOptions,
    vehicleTypeId,
    setValue,
    setError,
    watch,
    t,
    vehicleTypes,
    tripTypes,
    attachmentOptions,
  };
};

export default useEditVehicleOperationForm;
